import type {
  AcademicService,
  FeeLine,
  FulfilmentMode,
  RequestStatus,
  ServiceOrigin,
} from '../types/services'

/**
 * The origin label is the single most important thing on a service — it tells
 * the student who is actually responsible for the outcome.
 */
export const ORIGIN_LABEL: Record<ServiceOrigin, string> = {
  official: 'Official university service',
  assisted: 'Assisted by Campuzon',
  third_party: 'Third-party provider',
  integrated: 'Institutional integration',
}

export const ORIGIN_EXPLAINER: Record<ServiceOrigin, string> = {
  official:
    'Provided by the university itself. Campuzon explains the process and tracks it — it does not fulfil it.',
  assisted:
    'Campuzon coordinates a process the university owns. The university still makes every decision.',
  third_party:
    'Provided by an independent person or business verified by Campuzon. They hold no institutional authority.',
  integrated:
    'The university exposes this service through Campuzon under a formal agreement, so status updates flow back automatically.',
}

export const ORIGIN_TONE: Record<ServiceOrigin, 'ok' | 'brand' | 'neutral' | 'warn'> = {
  official: 'ok',
  integrated: 'ok',
  assisted: 'brand',
  third_party: 'neutral',
}

export const MODE_LABEL: Record<FulfilmentMode, string> = {
  official_digital: 'Do it yourself on the official channel',
  assisted: 'Get verified assistance',
  institutional_integration: 'Request through Campuzon',
}

/** Statuses grouped so the UI can colour them without a giant switch. */
const TERMINAL_GOOD: RequestStatus[] = ['Completed', 'Delivered', 'Collected']
const TERMINAL_BAD: RequestStatus[] = ['Rejected', 'Cancelled', 'Disputed']
const WAITING: RequestStatus[] = [
  'Awaiting information',
  'Awaiting payment',
  'Awaiting provider',
  'Awaiting institution',
]

export function statusTone(status: RequestStatus): 'ok' | 'warn' | 'danger' | 'brand' | 'neutral' {
  if (TERMINAL_GOOD.includes(status)) return 'ok'
  if (TERMINAL_BAD.includes(status)) return 'danger'
  if (WAITING.includes(status)) return 'warn'
  if (status === 'Draft') return 'neutral'
  return 'brand'
}

export function isOpen(status: RequestStatus) {
  return !TERMINAL_GOOD.includes(status) && !TERMINAL_BAD.includes(status)
}

/** Whose money is whose — never presented as one blended total. */
export function feeTotals(fees: FeeLine[]) {
  const known = fees.filter((fee) => !fee.unknown)
  const unknown = fees.filter((fee) => fee.unknown)
  return {
    institution: known.filter((f) => f.payee === 'institution').reduce((sum, f) => sum + f.amount, 0),
    provider: known.filter((f) => f.payee === 'provider').reduce((sum, f) => sum + f.amount, 0),
    courier: known.filter((f) => f.payee === 'courier').reduce((sum, f) => sum + f.amount, 0),
    campuzon: known.filter((f) => f.payee === 'campuzon').reduce((sum, f) => sum + f.amount, 0),
    total: known.reduce((sum, f) => sum + f.amount, 0),
    hasUnknown: unknown.length > 0,
  }
}

export const PAYEE_LABEL: Record<FeeLine['payee'], string> = {
  institution: 'Paid to the university',
  provider: 'Paid to the provider',
  courier: 'Paid to the courier',
  campuzon: 'Paid to Campuzon',
}

/** Free-text search across the catalogue, institution-aware. */
export function searchServices(services: AcademicService[], query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return services
  return services.filter((service) =>
    [service.name, service.category, service.description, service.officialProvider ?? '']
      .join(' ')
      .toLowerCase()
      .includes(q),
  )
}

/** Unknown values must read as unavailable, never as a guess. */
export function orUnavailable(value: string | null | undefined) {
  return value && value.length > 0 ? value : 'Information not currently available.'
}
